const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:3000/api/v1";

export interface Service {
  id: string;
  name: string;
  description: string;
  shortDescription: string;
  price: number;
  originalPrice?: number;
  duration: number;
  durationText: string;
  isActive: boolean;
  category: string;
  features: string[];
  includesPickup: boolean;
  popular: boolean;
  requirements?: string[];
  limitations?: string[];
  image?: string | null;
  imageUrl?: string | null;
  createdAt: string;
  updatedAt: string;
}

// Contact
export interface ContactPayload {
  name: string;
  email: string;
  phone?: string;
  subject?: string;
  message: string;
}
export interface ContactResponse {
  id: string;
}

export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data?: T;
  error?: string;
  timestamp: string;
}

export interface Zone {
  id: string;
  name: string;
  code: string;
  covered: boolean;
  latitude: number;
  longitude: number;
  createdAt: string;
  updatedAt: string;
}

export interface RelaySpot {
  id: string;
  name: string;
  address: string;
  city: string;
  postalCode: string;
  latitude?: number | null;
  longitude?: number | null;
  partner?: string | null;
  openingHours?: any;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

class ApiService {
  private async request<T>(
    endpoint: string,
    options?: RequestInit
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;

    try {
      const response = await fetch(url, {
        headers: {
          "Content-Type": "application/json",
          ...options?.headers,
        },
        ...options,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: ApiResponse<T> = await response.json();

      if (!data.success) {
        throw new Error(data.error || data.message);
      }

      return data.data as T;
    } catch (error) {
      console.error(`API request failed for ${endpoint}:`, error);
      throw error;
    }
  }

  // Services endpoints
  async getServices(params?: {
    category?: string;
    popular?: boolean;
    lang?: string;
  }): Promise<Service[]> {
    const searchParams = new URLSearchParams();

    if (params?.category) {
      searchParams.append("category", params.category);
    }

    if (params?.popular !== undefined) {
      searchParams.append("popular", params.popular.toString());
    }

    if (params?.lang) {
      searchParams.append("lang", params.lang);
    }

    const queryString = searchParams.toString();
    const endpoint = `/services${queryString ? `?${queryString}` : ""}`;

    return this.request<Service[]>(endpoint);
  }

  async getService(id: string, lang?: string): Promise<Service> {
    const searchParams = new URLSearchParams();
    if (lang) {
      searchParams.append("lang", lang);
    }
    const queryString = searchParams.toString();
    const endpoint = `/services/${id}${queryString ? `?${queryString}` : ""}`;
    return this.request<Service>(endpoint);
  }

  async getServicesByCategory(category: string, lang?: string): Promise<Service[]> {
    const searchParams = new URLSearchParams();
    if (lang) {
      searchParams.append("lang", lang);
    }
    const queryString = searchParams.toString();
    const endpoint = `/services/category/${category}${queryString ? `?${queryString}` : ""}`;
    return this.request<Service[]>(endpoint);
  }

  async getPopularServices(lang?: string): Promise<Service[]> {
    const searchParams = new URLSearchParams();
    if (lang) {
      searchParams.append("lang", lang);
    }
    const queryString = searchParams.toString();
    const endpoint = `/services/popular${queryString ? `?${queryString}` : ""}`;
    return this.request<Service[]>(endpoint);
  }

  // Zones endpoints
  async getZones(): Promise<Zone[]> {
    return this.request<Zone[]>("/zones");
  }

  // Relay spots endpoints
  async getRelaySpots(params?: {
    q?: string;
    city?: string;
    postalCode?: string;
    partner?: string;
    active?: boolean;
    page?: number;
    limit?: number;
  }): Promise<{ items: RelaySpot[]; pagination: any }> {
    const searchParams = new URLSearchParams();

    if (params?.q) searchParams.append("q", params.q);
    if (params?.city) searchParams.append("city", params.city);
    if (params?.postalCode) searchParams.append("postalCode", params.postalCode);
    if (params?.partner) searchParams.append("partner", params.partner);
    if (params?.active !== undefined)
      searchParams.append("active", String(params.active));
    if (params?.page) searchParams.append("page", String(params.page));
    if (params?.limit) searchParams.append("limit", String(params.limit));

    const queryString = searchParams.toString();
    const endpoint = `/relay-spots${queryString ? `?${queryString}` : ""}`;
    return this.request<{ items: RelaySpot[]; pagination: any }>(endpoint);
  }

  // Contact endpoint
  async submitContact(payload: ContactPayload): Promise<ContactResponse> {
    return this.request<ContactResponse>(`/contact`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }
}

export const apiService = new ApiService();
export default apiService;
